/**
 * Application permissions following the pattern:
 * - <FEATURE>_PAGE: For page access (sidebar + route + API GET)
 * - <FEATURE>_<ACTION>: For specific actions (button + API POST/PUT/DELETE)
 */

import { Permissions as BasePermissions } from 'dmsbasereact';

export const Permissions = {
    ...BasePermissions,

    //=========================================================================
    // PROFILE - Basic access for all authenticated users
    //=========================================================================
    PROFILE_PAGE: "PROFILE_PAGE",
    PROFILE_EDIT: "PROFILE_EDIT",

    //=========================================================================
    // PAGE PERMISSIONS - Controls sidebar + route + API GET
    //=========================================================================

    // Doctor pages
    MY_PATIENTS_PAGE: "MY_PATIENTS_PAGE",
    PRESCRIPTION_HISTORY_PAGE: "PRESCRIPTION_HISTORY_PAGE",
    PRESCRIPTION_DISPENSATION_OVERVIEW_PAGE: "PRESCRIPTION_DISPENSATION_OVERVIEW_PAGE",

    // Pharmacist pages
    DISPENSATION_PAGE: "DISPENSATION_PAGE",
    DISPENSATION_HISTORY_PAGE: "DISPENSATION_HISTORY_PAGE",

    // Institution Responsible pages
    MY_INSTITUTION_PAGE: "MY_INSTITUTION_PAGE",

    // Shared pages (multiple roles)
    INSTITUTION_PAGE: "INSTITUTION_PAGE",
    STOCK_PAGE: "STOCK_PAGE",
    STOCK_OVERVIEW_PAGE: "STOCK_OVERVIEW_PAGE",
    STOCK_MANAGEMENT_PAGE: "STOCK_MANAGEMENT_PAGE",
    TRACEABILITY_PAGE: "TRACEABILITY_PAGE",
    ANALYTICS_PAGE: "ANALYTICS_PAGE",
    REPORTS_PAGE: "REPORTS_PAGE",
    REPORTS_DETAILED: "REPORTS_DETAILED",
    REPORTS_EXECUTIVE: "REPORTS_EXECUTIVE",
    // Individual report permissions (granular control)
    REPORTS_SALES: "REPORTS_SALES",
    REPORTS_TRACEABILITY: "REPORTS_TRACEABILITY",
    REPORTS_QUALIFIED_CLIENTS: "REPORTS_QUALIFIED_CLIENTS",
    REPORTS_PRESCRIPTIONS_DISPENSATIONS: "REPORTS_PRESCRIPTIONS_DISPENSATIONS",
    REPORTS_PRESCRIPTIONS_DISPENSATIONS_COMBINED: "REPORTS_PRESCRIPTIONS_DISPENSATIONS_COMBINED",
    REPORTS_INSTITUTIONS: "REPORTS_INSTITUTIONS",
    REPORTS_DISPENSATIONS: "REPORTS_DISPENSATIONS",
    REPORTS_PRESCRIPTIONS: "REPORTS_PRESCRIPTIONS",
    REPORTS_PHARMACISTS: "REPORTS_PHARMACISTS",
    REPORTS_PATIENTS: "REPORTS_PATIENTS",
    REPORTS_DOCTORS: "REPORTS_DOCTORS",
    RETURN_PAGE: "RETURN_PAGE",
    PATIENT_PAGE: "PATIENT_PAGE",
    TRAINING_VIDEO_PAGE: "TRAINING_VIDEO_PAGE",

    // Admin pages
    ADMIN_PAGE: "ADMIN_PAGE",
    USER_MANAGEMENT_PAGE: "USER_MANAGEMENT_PAGE",
    PENDING_APPROVALS_PAGE: "PENDING_APPROVALS_PAGE",
    ROLES_PAGE: "ROLES_PAGE",
    SYSTEM_SETTINGS_PAGE: "SYSTEM_SETTINGS_PAGE",
    LOGS_REPORT_PAGE: "LOGS_REPORT_PAGE",
    ADVANCED_AUDIT_PAGE: "ADVANCED_AUDIT_PAGE",
    DATA_MANAGEMENT_PAGE: "DATA_MANAGEMENT_PAGE",
    IMPORT_PAGE: "IMPORT_PAGE",
    SAP_INTEGRATION_PAGE: "SAP_INTEGRATION_PAGE",
    SAP_HOMOLOGATED_INSTITUTIONS_PAGE: "SAP_HOMOLOGATED_INSTITUTIONS_PAGE",
    SAP_HOMOLOGATED_INSTITUTIONS_MANAGE: "SAP_HOMOLOGATED_INSTITUTIONS_MANAGE",
    SAP_MY_INSTITUTIONS_PAGE: "SAP_MY_INSTITUTIONS_PAGE",
    TRAINING_CENTRAL_PAGE: "TRAINING_CENTRAL_PAGE",
    COMPLETED_TRAININGS_PAGE: "COMPLETED_TRAININGS_PAGE",
    EMITTED_CERTIFICATES_PAGE: "EMITTED_CERTIFICATES_PAGE",
    ALERT_SYSTEM_PAGE: "ALERT_SYSTEM_PAGE",

    // Auditor pages
    AUDIT_PAGE: "AUDIT_PAGE",

    // Director Groups management (Gestão Lena only)
    DIRECTOR_GROUPS_PAGE: "DIRECTOR_GROUPS_PAGE",
    DIRECTOR_GROUPS_MANAGE: "DIRECTOR_GROUPS_MANAGE",

    // System Notices (Gestão Lena only)
    NOTICE_PAGE: "NOTICE_PAGE",
    NOTICE_MANAGE: "NOTICE_MANAGE",

    // Institution Partner Groups (Qualification Tasks 13.6-13.8, 13.12)
    INSTITUTION_PARTNER_GROUPS_PAGE: "INSTITUTION_PARTNER_GROUPS_PAGE",
    INSTITUTION_PARTNER_GROUPS_VIEW: "INSTITUTION_PARTNER_GROUPS_VIEW",
    INSTITUTION_PARTNER_GROUPS_MANAGE: "INSTITUTION_PARTNER_GROUPS_MANAGE",

    // Partner page (Gestão Lena + Comercial + Diretoria view-only)
    PARTNER_PAGE: "PARTNER_PAGE",
    PARTNER_LINK_CREATE: "PARTNER_LINK_CREATE",
    PARTNER_LINK_DELETE: "PARTNER_LINK_DELETE",

    // Branch page (Gestão Lena + Institution Responsible)
    BRANCH_PAGE: "BRANCH_PAGE",

    // Quality Return Review (Eurofarma Garantia)
    RETURN_QUALITY_PAGE: "RETURN_QUALITY_PAGE",

    // Return Management (Gestão Lena - complete return process)
    RETURN_MANAGEMENT_PAGE: "RETURN_MANAGEMENT_PAGE",

    //=========================================================================
    // ACTION PERMISSIONS - Controls buttons + API POST/PUT/DELETE
    //=========================================================================

    // Prescription actions
    PRESCRIPTION_CREATE: "PRESCRIPTION_CREATE",
    PRESCRIPTION_EDIT: "PRESCRIPTION_EDIT",
    PRESCRIPTION_CANCEL: "PRESCRIPTION_CANCEL",
    PRESCRIPTION_SIGN: "PRESCRIPTION_SIGN",

    // Patient actions
    PATIENT_CREATE: "PATIENT_CREATE",
    PATIENT_EDIT: "PATIENT_EDIT",

    // Dispensation actions
    DISPENSATION_CREATE: "DISPENSATION_CREATE",
    DISPENSATION_CONFIRM: "DISPENSATION_CONFIRM",
    DISPENSATION_CANCELLATION_APPROVE: "DISPENSATION_CANCELLATION_APPROVE",

    // Institution actions
    INSTITUTION_APPROVE: "INSTITUTION_APPROVE",
    INSTITUTION_REJECT: "INSTITUTION_REJECT",
    INSTITUTION_EDIT: "INSTITUTION_EDIT",
    INSTITUTION_USERS_MANAGE: "INSTITUTION_USERS_MANAGE",
    INSTITUTION_DEACTIVATE: "INSTITUTION_DEACTIVATE",
    // Institution Management (admin page to manage all institutions)
    INSTITUTION_MANAGEMENT_PAGE: "INSTITUTION_MANAGEMENT_PAGE",
    INSTITUTION_MANAGEMENT_EDIT: "INSTITUTION_MANAGEMENT_EDIT",
    INSTITUTION_MANAGEMENT_CREATE: "INSTITUTION_MANAGEMENT_CREATE",
    INSTITUTION_DOCUMENTS_UPLOAD: "INSTITUTION_DOCUMENTS_UPLOAD",

    // Stock actions
    STOCK_CHECKIN: "STOCK_CHECKIN",
    STOCK_CHECKOUT: "STOCK_CHECKOUT",
    STOCK_RECONCILE: "STOCK_RECONCILE",

    // Return actions
    RETURN_CREATE: "RETURN_CREATE",
    RETURN_CONFIRM: "RETURN_CONFIRM",
    RETURN_QUALITY_MANAGE: "RETURN_QUALITY_MANAGE",
    RETURN_MANAGEMENT_UPDATE: "RETURN_MANAGEMENT_UPDATE",

    // User management actions
    USER_APPROVE: "USER_APPROVE",
    USER_REJECT: "USER_REJECT",
    USER_ASSIGN_ROLE: "USER_ASSIGN_ROLE",
    USER_CREATE_INTERNAL: "USER_CREATE_INTERNAL",
    USER_CREATE_AUDITOR: "USER_CREATE_AUDITOR",
    USER_DOCUMENTS_UPLOAD: "USER_DOCUMENTS_UPLOAD",

    // Settings actions
    SETTINGS_API_KEYS: "SETTINGS_API_KEYS",
    SETTINGS_SMTP: "SETTINGS_SMTP",
    SETTINGS_SSO: "SETTINGS_SSO",
    SETTINGS_SECURITY: "SETTINGS_SECURITY",
    SETTINGS_LOG: "SETTINGS_LOG",
    SETTINGS_LOGIN: "SETTINGS_LOGIN",
    SETTINGS_GENERAL: "SETTINGS_GENERAL",
    SETTINGS_SAP: "SETTINGS_SAP",
    SETTINGS_HELP_CENTER: "SETTINGS_HELP_CENTER",
    SETTINGS_MANUAL_UPLOAD: "SETTINGS_MANUAL_UPLOAD",
    SETTINGS_LEGAL_DOCUMENTS: "SETTINGS_LEGAL_DOCUMENTS",
    SETTINGS_DEBUG: "SETTINGS_DEBUG",

    // Audit actions
    AUDIT_EXPORT: "AUDIT_EXPORT",

    //=========================================================================
    // LEGACY PERMISSIONS - Kept for backward compatibility
    // TODO: Remove after full migration to new pattern
    //=========================================================================

    // Old page access patterns
    DOCTOR_PAGES_ACCESS: "DOCTOR_PAGES_ACCESS",
    PHARMACIST_PAGES_ACCESS: "PHARMACIST_PAGES_ACCESS",
    INSTITUTION_RESPONSIBLE_ACCESS: "INSTITUTION_RESPONSIBLE_ACCESS",
    LENA_ADMIN_ACCESS: "LENA_ADMIN_ACCESS",

    // Old permission names
    PROFILE_VIEW: "PROFILE_VIEW",
    PRESCRIPTION_VIEW: "PRESCRIPTION_VIEW",
    DISPENSATION_VIEW: "DISPENSATION_VIEW",
    INSTITUTION_VIEW: "INSTITUTION_VIEW",
    STOCK_VIEW: "STOCK_VIEW",
    ANALYTICS_VIEW: "ANALYTICS_VIEW",
    RETURN_ACCESS: "RETURN_ACCESS",
    PATIENT_MANAGE: "PATIENT_MANAGE",

    // Old admin permissions
    USER_MANAGEMENT_ACCESS: "USER_MANAGEMENT_ACCESS",
    USER_VIEW: "USER_VIEW",
    EXECUTIVE_DASHBOARD_ACCESS: "EXECUTIVE_DASHBOARD_ACCESS",
    REPORTS_VIEW: "REPORTS_VIEW",
    REGULATORY_ACCESS: "REGULATORY_ACCESS",
    REGULATORY_REPORTS_VIEW: "REGULATORY_REPORTS_VIEW",
    REGULATORY_COMPLIANCE_MANAGE: "REGULATORY_COMPLIANCE_MANAGE",
    COMMERCIAL_ACCESS: "COMMERCIAL_ACCESS",
    COMMERCIAL_REPORTS_VIEW: "COMMERCIAL_REPORTS_VIEW",
    COMMERCIAL_PARTNERS_MANAGE: "COMMERCIAL_PARTNERS_MANAGE",
    QUALITY_ACCESS: "QUALITY_ACCESS",
    QUALITY_REPORTS_VIEW: "QUALITY_REPORTS_VIEW",
    BATCH_QUALITY_MANAGE: "BATCH_QUALITY_MANAGE",
    CENTRAL_ACCESS: "CENTRAL_ACCESS",
    CENTRAL_OPERATIONS_VIEW: "CENTRAL_OPERATIONS_VIEW",
    SUPPORT_TICKETS_MANAGE: "SUPPORT_TICKETS_MANAGE",
    AUDIT_ACCESS: "AUDIT_ACCESS",
    AUDIT_LOGS_VIEW: "AUDIT_LOGS_VIEW",
    AUDIT_PRESCRIPTIONS_VIEW: "AUDIT_PRESCRIPTIONS_VIEW",
    AUDIT_DISPENSATIONS_VIEW: "AUDIT_DISPENSATIONS_VIEW",
    AUDIT_REPORTS_EXPORT: "AUDIT_REPORTS_EXPORT",

    // Old settings permissions
    CAN_MANAGE_SMTP_SETTINGS: "CAN_MANAGE_SMTP_SETTINGS",
    CAN_MANAGE_SSO_SETTINGS: "CAN_MANAGE_SSO_SETTINGS",
    CAN_MANAGE_SECURITY_SETTINGS: "CAN_MANAGE_SECURITY_SETTINGS",
    CAN_MANAGE_API_KEYS: "CAN_MANAGE_API_KEYS",
    CAN_MANAGE_LOG_SETTINGS: "CAN_MANAGE_LOG_SETTINGS",
    CAN_MANAGE_LOGIN_SETTINGS: "CAN_MANAGE_LOGIN_SETTINGS",
    CAN_MANAGE_GENERAL_SETTINGS: "CAN_MANAGE_GENERAL_SETTINGS",
    CAN_MANAGE_SAP_SETTINGS: "CAN_MANAGE_SAP_SETTINGS",
    CAN_MANAGE_HELP_CENTER: "CAN_MANAGE_HELP_CENTER",
    CAN_UPLOAD_MANUAL: "CAN_UPLOAD_MANUAL",
    CAN_MANAGE_EMAIL_TEMPLATES: "CAN_MANAGE_EMAIL_TEMPLATES",
    CAN_MANAGE_LEGAL_DOCUMENTS: "CAN_MANAGE_LEGAL_DOCUMENTS",

    CAN_VIEW_ALERT_SMTP_SETTINGS: "CAN_VIEW_ALERT_SMTP_SETTINGS",
    CAN_EDIT_ALERT_SMTP_SETTINGS: "CAN_EDIT_ALERT_SMTP_SETTINGS",

    // Eurofarma role markers
    EUROFARMA_MANAGEMENT: "EUROFARMA_MANAGEMENT",
    EUROFARMA_SIGNING: "EUROFARMA_SIGNING",
    EUROFARMA_COMMERCIAL: "EUROFARMA_COMMERCIAL",
    EUROFARMA_REGULATORY: "EUROFARMA_REGULATORY",
    EUROFARMA_CENTRAL_READONLY: "EUROFARMA_CENTRAL_READONLY",
    EUROFARMA_QUALITY_READONLY: "EUROFARMA_QUALITY_READONLY",
    EUROFARMA_READONLY: "EUROFARMA_READONLY",
    EUROFARMA_GESTAO_LENA: "EUROFARMA_GESTAO_LENA",
};
